import { ListItem } from "@mui/material";

import { List } from "@/Components/v2/atoms/List";
import { Stack } from "@/Components/v2/atoms/Stack";
import { BoldSpan, Typography } from "@/Components/v2/atoms/Typography";
import { LabelType } from "@/hooks/useConfig";
import { formatPercentage } from "@/pages/scenario-builder/utils";
import { calculateROI } from "@/shared/utils/calculateROI";
import { calculateTotalChannelROI } from "@/shared/utils/calculateTotalChannelROI";

import { BreakdownDataProps } from "../BreakdownChart";
import { ChannelTotals } from "../BreakdownChartToolTip";

interface ToolTipROIProps {
  data: { seriesName: string; channel: string; value: string | number; color: string }[];
  mappedData: BreakdownDataProps[];
  channelTotals: ChannelTotals;
  hoveredChartIndex: number;
  label: LabelType;
}

const ToolTipROI: React.FC<ToolTipROIProps> = ({
  data,
  mappedData,
  label,
  channelTotals,
  hoveredChartIndex,
}) => {
  const channel = data[0]?.channel || "";
  const channelROI = calculateTotalChannelROI(
    channel,
    channelTotals as { [key: string]: { totalRevenue?: number; totalSpend?: number } }
  );

  // Calculate ROI values for each category, handling cases where they might be non-existent
  const brand = calculateROI(
    mappedData[hoveredChartIndex]?.spend[0] ?? 0,
    mappedData[hoveredChartIndex]?.revenue[0] ?? 0
  );
  const category =
    mappedData[hoveredChartIndex]?.spend[1] !== undefined &&
    mappedData[hoveredChartIndex]?.revenue[1] !== undefined
      ? calculateROI(
          mappedData[hoveredChartIndex]!.spend[1],
          mappedData[hoveredChartIndex]!.revenue[1]
        )
      : null;
  const product =
    mappedData[hoveredChartIndex]?.spend[2] !== undefined &&
    mappedData[hoveredChartIndex]?.revenue[2] !== undefined
      ? calculateROI(
          mappedData[hoveredChartIndex]!.spend[2],
          mappedData[hoveredChartIndex]!.revenue[2]
        )
      : null;

  return (
    <>
      {/* ROI Layers */}
      <List>
        <ListItem>
          <Typography variant="body1" fontWeight={600}>
            Marketing ROI
          </Typography>
        </ListItem>
        <ListItem>
          <Stack>
            <Typography variant="body2">
              <BoldSpan>{label.brand}</BoldSpan>
            </Typography>
            <Typography>{formatPercentage(brand, true)}</Typography>
          </Stack>
        </ListItem>
        {category !== null && (
          <ListItem>
            <Stack>
              <Typography variant="body2">
                <BoldSpan>{label.category}</BoldSpan>
              </Typography>
              <Typography>{formatPercentage(category, true)}</Typography>
            </Stack>
          </ListItem>
        )}
        {product !== null && (
          <ListItem>
            <Stack>
              <Typography variant="body2">
                <BoldSpan>{label.product}</BoldSpan>
              </Typography>
              <Typography>{formatPercentage(product, true)}</Typography>
            </Stack>
          </ListItem>
        )}
      </List>

      {/* Total ROI */}
      <ListItem>
        <Stack>
          <Typography variant="body2">
            <BoldSpan>Total</BoldSpan>
          </Typography>
          <Typography>{formatPercentage(channelROI, true)}</Typography>
        </Stack>
      </ListItem>
    </>
  );
};

export default ToolTipROI;
